import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { mandantenAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import {
  CalendarClock, Users, Download, RefreshCw, Search, Trash2, Loader2,
  ExternalLink, StickyNote, CalendarOff, X, BookOpen,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

/* Termine & Leads je Mandant (Zinzino / PTL).
   Statuswerte und Farben kommen aus /api/mandanten — nichts hartkodiert.
   Es gibt bewusst keine mandantenübergreifende Ansicht. */

const STATUS_LABEL = {
  gebucht: 'Gebucht', bestaetigt: 'Bestätigt', wahrgenommen: 'Wahrgenommen',
  test_uebergeben: 'Test übergeben', kunde: 'Kunde', abschluss: 'Abschluss',
  nicht_erschienen: 'Nicht erschienen', abgesagt: 'Abgesagt',
  neu: 'Neu', kontaktiert: 'Kontaktiert', termin: 'Termin', kein_interesse: 'Kein Interesse',
};

const fmtZeit = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
};
const fmtDatum = (d) => { const [y, m, t] = (d || '').split('-'); return t ? `${t}.${m}.${y}` : d; };

function StatusSelect({ wert, werte, farben, onChange }) {
  return (
    <select value={wert} onChange={(e) => onChange(e.target.value)}
            className="text-xs font-medium rounded-md border border-border bg-background px-2 py-1"
            style={{ color: farben?.[wert] }}>
      {werte.map((s) => <option key={s} value={s}>{STATUS_LABEL[s] || s}</option>)}
    </select>
  );
}

export default function MandantenPage() {
  const { mandant = 'zinzino' } = useParams();
  const navigate = useNavigate();
  const { userPermissions, isAdminPermissions } = useAuth();
  const darfVerwalten = isAdminPermissions || !!userPermissions?.['mandanten.verwalten'];

  const [mandanten, setMandanten] = useState([]);
  const [termine, setTermine] = useState([]);
  const [leads, setLeads] = useState([]);
  const [sperren, setSperren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('termine');
  const [zeitraum, setZeitraum] = useState('kommend');
  const [suche, setSuche] = useState('');
  const [notizFuer, setNotizFuer] = useState(null);   // {typ, id, name}
  const [notizText, setNotizText] = useState('');
  const [sperrDatum, setSperrDatum] = useState('');

  const cfg = useMemo(() => mandanten.find((m) => m.id === mandant), [mandanten, mandant]);

  const laden = useCallback(async (still = false) => {
    if (!still) setLoading(true);
    try {
      const [m, t, l, sp] = await Promise.all([
        mandantenAPI.liste(),
        mandantenAPI.termine({ mandant, zeitraum: zeitraum === 'alle' ? '' : zeitraum, q: suche }),
        mandantenAPI.leads({ mandant, q: suche }),
        mandantenAPI.sperren(mandant),
      ]);
      setMandanten(m.data); setTermine(t.data); setLeads(l.data); setSperren(sp.data);
    } catch (e) {
      if (e?.response?.status === 404) navigate('/mandanten/zinzino', { replace: true });
      else toast.error('Daten konnten nicht geladen werden.');
    }
    setLoading(false);
  }, [mandant, zeitraum, suche, navigate]);
  useEffect(() => { laden(); }, [laden]);

  const terminStatus = async (t, status) => {
    try { await mandantenAPI.terminUpdate(t.id, { status }); await laden(true); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Änderung fehlgeschlagen.'); }
  };
  const leadStatus = async (l, status) => {
    try { await mandantenAPI.leadUpdate(l.id, { status }); await laden(true); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Änderung fehlgeschlagen.'); }
  };

  const loeschen = async (typ, obj) => {
    if (!window.confirm(`${obj.vorname} ${obj.nachname} endgültig löschen (DSGVO Art. 17)? Das kann nicht rückgängig gemacht werden.`)) return;
    try {
      if (typ === 'termin') await mandantenAPI.terminLoeschen(obj.id);
      else await mandantenAPI.leadLoeschen(obj.id);
      toast.success('Endgültig gelöscht.');
      await laden(true);
    } catch { toast.error('Löschen fehlgeschlagen.'); }
  };

  const notizSpeichern = async () => {
    if (!notizText.trim()) return;
    try {
      if (notizFuer.typ === 'termin') await mandantenAPI.terminUpdate(notizFuer.id, { notiz: notizText.trim() });
      else await mandantenAPI.leadUpdate(notizFuer.id, { notiz: notizText.trim() });
      setNotizFuer(null); setNotizText('');
      await laden(true);
    } catch { toast.error('Notiz konnte nicht gespeichert werden.'); }
  };

  const csv = async (typ) => {
    try {
      const res = typ === 'termine' ? await mandantenAPI.termineCSV(mandant) : await mandantenAPI.leadsCSV(mandant);
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv;charset=utf-8' }));
      const a = document.createElement('a');
      a.href = url; a.download = `${typ}-${mandant}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Export fehlgeschlagen.'); }
  };

  const sperrenAnlegen = async () => {
    if (!sperrDatum) return;
    try { await mandantenAPI.sperreAnlegen(mandant, sperrDatum); setSperrDatum(''); await laden(true); }
    catch { toast.error('Sperrtag konnte nicht angelegt werden.'); }
  };

  if (loading && !cfg) {
    return <div className="flex items-center justify-center py-24 text-muted-foreground">
      <Loader2 className="h-5 w-5 animate-spin mr-2" /> Lädt …
    </div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <CalendarClock className="h-6 w-6 text-marke" /> Termine & Leads
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {cfg ? `${cfg.name} · ${cfg.berater} · ${cfg.termin_dauer_min} Min` : ''}
          </p>
        </div>
        {/* Mandanten-Umschalter */}
        <div className="flex rounded-lg border border-border overflow-hidden">
          {mandanten.map((m) => (
            <button key={m.id} onClick={() => navigate(`/mandanten/${m.id}`)}
                    className={`px-4 py-2 text-sm font-medium ${m.id === mandant
                      ? 'bg-primary text-primary-foreground' : 'bg-background text-muted-foreground hover:bg-accent'}`}>
              {m.id === 'zinzino' ? 'Zinzino' : 'PTL'}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input className="pl-9" placeholder="Name, E-Mail oder Telefon …" value={suche}
                 onChange={(e) => setSuche(e.target.value)} />
        </div>
        <Button variant="outline" size="sm" onClick={() => laden(true)}><RefreshCw className="h-4 w-4 mr-1.5" /> Aktualisieren</Button>
        <Button variant="outline" size="sm" asChild>
          <a href={mandant === 'zinzino' ? '/zinzino' : '/ptl'} target="_blank" rel="noreferrer">
            <ExternalLink className="h-4 w-4 mr-1.5" /> Landingpage
          </a>
        </Button>
        {mandant === 'zinzino' && darfVerwalten && (
          <Button size="sm" onClick={() => navigate('/zinzino-mappe')}
                  className="bg-marke hover:bg-marke-dunkel text-white">
            <BookOpen className="h-4 w-4 mr-1.5" /> Verkaufsmappe
          </Button>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="termine"><CalendarClock className="h-4 w-4 mr-1.5" /> Termine ({termine.length})</TabsTrigger>
          <TabsTrigger value="leads"><Users className="h-4 w-4 mr-1.5" /> Leads ({leads.length})</TabsTrigger>
          <TabsTrigger value="sperren"><CalendarOff className="h-4 w-4 mr-1.5" /> Sperrtage ({sperren.length})</TabsTrigger>
        </TabsList>

        {/* ── Termine ── */}
        <TabsContent value="termine" className="mt-4">
          <Card>
            <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 space-y-0">
              <div className="flex gap-1">
                {[['kommend', 'Kommend'], ['vergangen', 'Vergangen'], ['alle', 'Alle']].map(([w, label]) => (
                  <Button key={w} variant={zeitraum === w ? 'default' : 'outline'} size="sm"
                          onClick={() => setZeitraum(w)}>{label}</Button>
                ))}
              </div>
              <Button variant="outline" size="sm" onClick={() => csv('termine')}>
                <Download className="h-4 w-4 mr-1.5" /> CSV
              </Button>
            </CardHeader>
            <CardContent>
              {termine.length === 0 ? (
                <p className="text-center py-10 text-muted-foreground">Keine Termine.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b text-left text-muted-foreground">
                      <th className="py-2 pr-3 font-medium">Termin</th>
                      <th className="py-2 pr-3 font-medium">Name</th>
                      <th className="py-2 pr-3 font-medium">Kontakt</th>
                      <th className="py-2 pr-3 font-medium">Status</th>
                      <th className="py-2 pr-3 font-medium">Notizen</th>
                      <th className="py-2 font-medium text-right">Aktion</th>
                    </tr></thead>
                    <tbody>
                      {termine.map((t) => (
                        <tr key={t.id} className="border-b last:border-0 align-top">
                          <td className="py-2.5 pr-3 whitespace-nowrap font-medium">
                            {fmtDatum(t.datum)}<span className="text-muted-foreground"> · {t.uhrzeit}</span>
                          </td>
                          <td className="py-2.5 pr-3">{t.vorname} {t.nachname}</td>
                          <td className="py-2.5 pr-3 text-muted-foreground">
                            <a href={`tel:${t.telefon}`} className="hover:underline block">{t.telefon}</a>
                            <a href={`mailto:${t.email}`} className="hover:underline block text-xs">{t.email}</a>
                          </td>
                          <td className="py-2.5 pr-3">
                            {darfVerwalten && cfg
                              ? <StatusSelect wert={t.status} werte={cfg.status_werte} farben={cfg.status_farben}
                                              onChange={(s) => terminStatus(t, s)} />
                              : <Badge variant="outline" style={{ color: cfg?.status_farben?.[t.status] }}>{STATUS_LABEL[t.status] || t.status}</Badge>}
                          </td>
                          <td className="py-2.5 pr-3 text-xs text-muted-foreground max-w-[220px]">
                            {(t.notizen || []).map((n, i) => <p key={i}>• {n.text}</p>)}
                          </td>
                          <td className="py-2.5 text-right whitespace-nowrap">
                            {darfVerwalten && <>
                              <Button variant="ghost" size="sm" onClick={() => { setNotizFuer({ typ: 'termin', id: t.id, name: `${t.vorname} ${t.nachname}` }); setNotizText(''); }}>
                                <StickyNote className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-destructive"
                                      onClick={() => loeschen('termin', t)}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Leads ── */}
        <TabsContent value="leads" className="mt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle className="text-base">Kontakte ohne Termin</CardTitle>
              <Button variant="outline" size="sm" onClick={() => csv('leads')}>
                <Download className="h-4 w-4 mr-1.5" /> CSV
              </Button>
            </CardHeader>
            <CardContent>
              {leads.length === 0 ? (
                <p className="text-center py-10 text-muted-foreground">Keine Leads.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b text-left text-muted-foreground">
                      <th className="py-2 pr-3 font-medium">Name</th>
                      <th className="py-2 pr-3 font-medium">Kontakt</th>
                      <th className="py-2 pr-3 font-medium">Quelle</th>
                      <th className="py-2 pr-3 font-medium">Status</th>
                      <th className="py-2 pr-3 font-medium">Notizen</th>
                      <th className="py-2 font-medium text-right">Aktion</th>
                    </tr></thead>
                    <tbody>
                      {leads.map((l) => (
                        <tr key={l.id} className="border-b last:border-0 align-top">
                          <td className="py-2.5 pr-3 font-medium">{l.vorname} {l.nachname}
                            <p className="text-xs text-muted-foreground font-normal">{fmtZeit(l.created_at)}</p>
                          </td>
                          <td className="py-2.5 pr-3 text-muted-foreground">
                            {l.telefon && <a href={`tel:${l.telefon}`} className="hover:underline block">{l.telefon}</a>}
                            <a href={`mailto:${l.email}`} className="hover:underline block text-xs">{l.email}</a>
                          </td>
                          <td className="py-2.5 pr-3 text-muted-foreground">{l.quelle}</td>
                          <td className="py-2.5 pr-3">
                            {darfVerwalten && cfg
                              ? <StatusSelect wert={l.status} werte={cfg.lead_status_werte} farben={cfg.lead_status_farben}
                                              onChange={(s) => leadStatus(l, s)} />
                              : <Badge variant="outline">{STATUS_LABEL[l.status] || l.status}</Badge>}
                          </td>
                          <td className="py-2.5 pr-3 text-xs text-muted-foreground max-w-[220px]">
                            {(l.notizen || []).map((n, i) => <p key={i}>• {n.text}</p>)}
                          </td>
                          <td className="py-2.5 text-right whitespace-nowrap">
                            {darfVerwalten && <>
                              <Button variant="ghost" size="sm" onClick={() => { setNotizFuer({ typ: 'lead', id: l.id, name: `${l.vorname} ${l.nachname}` }); setNotizText(''); }}>
                                <StickyNote className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-destructive"
                                      onClick={() => loeschen('lead', l)}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Sperrtage ── */}
        <TabsContent value="sperren" className="mt-4">
          <Card>
            <CardHeader><CardTitle className="text-base">Gesperrte Tage (keine Online-Buchung)</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              {darfVerwalten && (
                <div className="flex gap-2">
                  <Input type="date" value={sperrDatum} onChange={(e) => setSperrDatum(e.target.value)} className="max-w-[200px]" />
                  <Button size="sm" onClick={sperrenAnlegen} disabled={!sperrDatum}>Tag sperren</Button>
                </div>
              )}
              {sperren.length === 0 ? (
                <p className="text-muted-foreground text-sm">Keine Sperrtage.</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {sperren.map((s) => (
                    <Badge key={s.datum} variant="secondary" className="gap-1.5 py-1.5 px-3">
                      {fmtDatum(s.datum)}
                      {darfVerwalten && (
                        <button onClick={async () => { await mandantenAPI.sperreLoeschen(mandant, s.datum); laden(true); }}>
                          <X className="h-3 w-3" />
                        </button>
                      )}
                    </Badge>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Notiz-Dialog */}
      <Dialog open={!!notizFuer} onOpenChange={(o) => !o && setNotizFuer(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Notiz — {notizFuer?.name}</DialogTitle></DialogHeader>
          <Input value={notizText} onChange={(e) => setNotizText(e.target.value)} placeholder="Kurze Notiz …"
                 onKeyDown={(e) => e.key === 'Enter' && notizSpeichern()} autoFocus />
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" onClick={() => setNotizFuer(null)}>Abbrechen</Button>
            <Button onClick={notizSpeichern} disabled={!notizText.trim()}>Speichern</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
